import React, { useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router'

import { useEditor } from '@toasttab/sites-components'
import classnames from 'classnames'
import { validate } from 'uuid'
import { t } from 'src/translations'

import { PaginatedMenuItemsQuery } from 'src/apollo/onlineOrdering'
import { CtaType, LogoSizeOption } from 'src/apollo/sites'
import { isPreviewSite } from 'src/lib/js/previewModeUtils'
import { useStaticSiteContext } from 'src/public/components/app/StaticSiteContext'
import { useScrollNavBehavior } from 'src/lib/js/hooks/useScrollNavBehavior'
import { Channel } from 'src/public/js/siteUtilities'

import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import {
  RestaurantSiteContent,
  useRestaurant
} from 'shared/components/common/restaurant_context/RestaurantContext'

import { CTAData } from 'public/components/default_template/ctas'
import Footer from 'public/components/default_template/footer'
import { useMenuNav } from 'public/components/default_template/menu_nav/MenuNav'
import PageMeta from 'public/components/default_template/meta/PageMeta'
import Nav from 'public/components/default_template/nav/Nav'
import { useOOMenus } from 'public/components/online_ordering/useOOMenu'

import { OrderPageContexts } from './OrderPageContexts'
import OOFallback500 from 'shared/components/errors/OOFallback500'
import NotFound404 from 'shared/components/errors/NotFound404'
import { A11yProvider } from 'src/public/components/online_ordering/useA11y'

export type OrderPageContentProps = {
  scrollNavRef?: React.Ref<HTMLDivElement>
  hasHero?: boolean
  hideHero?: boolean
  usingSecondaryNav?: boolean
  menuGuid?: string
  groupGuid?: string
  itemSlug?: string
  itemGuid?: string
  itemGroupGuid?: string
  variationSlug?: string
  groupings?: PaginatedMenuItemsQuery['paginatedMenuItems']['groupings']
  hideToastLocalFulfillmentPanel?: boolean
}

type Params = {
  menuGuid?: string
  groupGuid?: string
  itemSlug?: string
  guid?: string
  groupSlug?: string
  variationSlug?: string
}

type Props = {
  channel: Channel
  children:
    | ((props: OrderPageContentProps) => React.ReactElement)
    | React.ReactElement
}

const getGuidsFromHash = (
  hash?: string
): { menuGuid?: string; groupGuid?: string } => {
  if (hash) {
    // anchored deeplinks are two guids mashed together, one after another,
    // each prepended with a single 'd' character. We remove the leading '#',
    // then split them apart
    const menuGuid = hash.slice(2, hash.length / 2 + 1)
    const groupGuid = hash.slice(hash.length / 2 + 2)
    return {
      menuGuid: validate(menuGuid) ? menuGuid : undefined,
      groupGuid: validate(groupGuid) ? groupGuid : undefined
    }
  }

  return {}
}

const OrderPageWrapper = ({
  params,
  children,
  channel
}: { params: Params } & Props) => {
  const {
    restaurant: siteRestaurant,
    selectedLocation,
    ooRestaurant: restaurant,
    toastProduct
  } = useRestaurant()

  const staticContext = useStaticSiteContext()
  const { scrollNavRef, usingSecondaryNav } = useScrollNavBehavior()

  useEffect(() => {
    require('smoothscroll-polyfill').polyfill()
  }, [])

  const {
    menus,
    loading: loadingMenus,
    groupings
  } = useOOMenus({
    skipPopularItems: channel === Channel.ECOMM && !!params.itemSlug
  })

  const { refMenuPages } = useMenuNav({ menus, page: '/order' })

  const { title, description } = useMemo(() => {
    const formattedMenuGroups = refMenuPages
      .slice(0, 3)
      .flatMap((menu) => menu.groups)
      .slice(0, 3)
      .map((group) => group.name)
      .join(', ')
    // Source the location-specific page seo meta override if it exists
    const title =
      selectedLocation?.orderPageConfigOverride?.seoMeta?.title ||
      siteRestaurant.config.ooConfig?.seoMeta?.title ||
      t('order-page-wrapper.order-online') ||
      undefined
    const description: string | undefined =
      selectedLocation?.orderPageConfigOverride?.seoMeta?.description ||
      siteRestaurant.config.ooConfig?.seoMeta?.description ||
      t('order-page-wrapper.order-online-description', {
        restaurantName: selectedLocation?.name || restaurant?.name || '',
        menuGroups: formattedMenuGroups
      }) ||
      undefined
    return { title, description }
  }, [
    refMenuPages,
    restaurant?.name,
    selectedLocation.name,
    selectedLocation?.orderPageConfigOverride?.seoMeta?.description,
    siteRestaurant.config.ooConfig?.seoMeta?.description,
    selectedLocation?.orderPageConfigOverride?.seoMeta?.title,
    siteRestaurant.config.ooConfig?.seoMeta?.title
  ])

  if (!restaurant) {
    return (
      <OOFallback500
        meta={siteRestaurant?.meta}
        siteTheme={siteRestaurant?.theme}
        rxLocation={selectedLocation}
        rxName={siteRestaurant?.name}
      />
    )
  }
  if (!menus && loadingMenus) {
    return <LoadingSpinnerOverlay />
  }
  if (!restaurant?.shortUrl) {
    return <NotFound404 />
  }

  const { meta, content, theme } = siteRestaurant
  if (!content || !refMenuPages) {
    return <NotFound404 meta={meta} siteTheme={theme} />
  }

  const usesOOBasic = siteRestaurant?.config?.hasFullCustomization === false // only a value of 'false' indicates OO Basic tier; true, null, and undefined indicate OO Pro

  const { primaryCta, nonPrimaryCtas } = content as RestaurantSiteContent
  const giftCardCTA: Omit<CTAData, 'submenus'> = {
    type: CtaType.Link,
    text: t('order-page-wrapper.buy-gift-cards'),
    link: restaurant.giftCardLinks.purchaseLink
  }
  const newNonPrimaryCtas =
    restaurant.giftCardLinks.purchaseLink && usesOOBasic
      ? [...(nonPrimaryCtas || []), giftCardCTA]
      : nonPrimaryCtas
  const hasHero = !!siteRestaurant.config.ooConfig?.heroImage?.src
  return (
    <>
      <PageMeta title={title} description={description} />
      <div
        className={classnames('defaultTemplate', {
          ecommPage: channel === Channel.ECOMM
        })}
      >
        <Nav
          withShadow={!usingSecondaryNav && channel !== Channel.ECOMM}
          logoSrc={meta.icon}
          logoObject={meta.iconObject}
          logoSize={LogoSizeOption.Standard}
          navType='stickyNav'
          primaryCta={primaryCta}
          nonPrimaryCtas={
            isPreviewSite(staticContext, siteRestaurant.config, toastProduct)
              ? []
              : newNonPrimaryCtas
          }
          forceDisplayToastLogin
          withCart
          withOOOptions
          shouldShowPreviewBanner={false}
        />
        {typeof children === 'function'
          ? children({
              scrollNavRef,
              hasHero,
              usingSecondaryNav,
              menuGuid: params.menuGuid,
              groupGuid: params.groupGuid,
              itemSlug: params.itemSlug,
              itemGuid: params.itemSlug?.split('_')?.[1] ?? params.guid,
              itemGroupGuid: params.groupSlug?.split('_')?.[1] ?? params.guid,
              variationSlug: params.variationSlug,
              groupings
            })
          : children}
        <Footer></Footer>
      </div>
    </>
  )
}

const ParamDetectingOrderPage = (props: Props) => {
  const params = useParams<Params>()
  const { hash } = useLocation()
  const anchorGuids = getGuidsFromHash(hash)
  return (
    <MemoizedOrderPageWrapper
      {...props}
      params={{
        menuGuid: params.menuGuid || anchorGuids.menuGuid,
        groupGuid: anchorGuids.groupGuid,
        itemSlug: params.itemSlug,
        guid: params.guid,
        groupSlug: params.groupSlug,
        variationSlug: params.variationSlug
      }}
    />
  )
}

const MemoizedOrderPageWrapper = React.memo(OrderPageWrapper)

export const ContextualOrderPageWrapper = (props: Props) => {
  const { isEditor } = useEditor()

  return (
    <A11yProvider>
      <OrderPageContexts channel={props.channel}>
        {isEditor ? (
          <MemoizedOrderPageWrapper params={{}} {...props} />
        ) : (
          <ParamDetectingOrderPage {...props} />
        )}
      </OrderPageContexts>
    </A11yProvider>
  )
}

export default React.memo(ContextualOrderPageWrapper)
